import {MutableRefObject, useEffect, useRef, useState} from 'react';

import {isExplicitBold, queryCommandState} from '../utils/editorCommands';
import {
  RichTextBlockFormat,
  getCurrentSelectionRange,
  getSelectedFormat,
  getSelectionPath,
} from '../utils/selectionHelpers';

export type ActiveCommands = Record<string, boolean>;

export function useEditorSelection(editorRef: MutableRefObject<HTMLDivElement | null>) {
  const selectionRef = useRef<Range | null>(null);
  const [selectionPath, setSelectionPath] = useState('');
  const [activeCommands, setActiveCommands] = useState<ActiveCommands>({});
  const [selectedFormat, setSelectedFormat] = useState<RichTextBlockFormat>('p');

  const updateMetadata = () => {
    const node = editorRef.current;

    if (!node) {
      return;
    }

    selectionRef.current = getCurrentSelectionRange(node);
    setSelectionPath(getSelectionPath(node));
    setActiveCommands({
      bold: isExplicitBold(node),
      italic: queryCommandState('italic'),
      underline: queryCommandState('underline'),
      strikeThrough: queryCommandState('strikeThrough'),
      insertUnorderedList: queryCommandState('insertUnorderedList'),
      insertOrderedList: queryCommandState('insertOrderedList'),
      justifyLeft: queryCommandState('justifyLeft'),
      justifyCenter: queryCommandState('justifyCenter'),
      justifyRight: queryCommandState('justifyRight'),
      justifyFull: queryCommandState('justifyFull'),
    });
    setSelectedFormat(getSelectedFormat(node));
  };

  useEffect(() => {
    const handleSelectionChange = () => {
      const node = editorRef.current;

      if (!node) {
        return;
      }

      const selection = window.getSelection();

      if (!selection || selection.rangeCount < 1) {
        return;
      }

      const anchorNode = selection.anchorNode;

      if (!anchorNode || !node.contains(anchorNode)) {
        return;
      }

      selectionRef.current = selection.getRangeAt(0).cloneRange();
      updateMetadata();
    };

    document.addEventListener('selectionchange', handleSelectionChange);

    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange);
    };
  }, []);

  return {
    selectionRef,
    selectionPath,
    activeCommands,
    selectedFormat,
    updateMetadata,
  };
}
